import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { IconShadow } from '@tabler/icons-react';
import { Popover, Transition } from '@headlessui/react';
import tinycolor from 'tinycolor2';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { brandKit } from '@/stores/brandKit';
import { updateCaptionStyles } from '@/stores/clip';
import { classnames } from '@/libs/utils';
import Toggle from '@/components/atoms/Toggle';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

function CaptionShadowPopover() {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const [shadowBlur, setShadowBlur] = useState<number>(20);
  const [shadowColor, setShadowColor] = useState<string>('#000000');
  const [shadowOpacity, setShadowOpacity] = useState<number>(100);

  const enabled = useMemo(() => {
    return !!clipData.asset_metadata.caption_styles?.pageStyle?.textShadow;
  }, [clipData.asset_metadata.caption_styles?.pageStyle?.textShadow]);

  const { currentBlur, currentColor, currentOpacity } = useMemo(() => {
    const textShadow = clipData.asset_metadata.caption_styles?.pageStyle?.textShadow || '0px 0px 0px #000000';

    // Extract blur and color using regex that handles both hex and rgba formats
    // Matches: "4px 4px 20px #000000" or "4px 4px 20px rgba(0, 0, 0, 0.5)"
    const match = textShadow.match(/(\d+px)\s+(\d+px)\s+(\d+px)\s+(.+)$/);
    const blurValue = match ? parseInt(match[3]) : 0;
    const colorValue = match ? match[4] : '#000000';

    const parsedColor = tinycolor(colorValue);
    const rgbValues = parsedColor.toRgb();
    const hexColor = parsedColor.toHex();
    const alphaPercentage = Math.round(rgbValues.a * 100);

    return {
      currentBlur: blurValue,
      currentColor: `#${hexColor}`,
      currentOpacity: alphaPercentage
    };
  }, [clipData.asset_metadata.caption_styles?.pageStyle?.textShadow]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (currentBlur > 0) {
      setShadowBlur(currentBlur);
    }

    if (currentColor) {
      setShadowColor(currentColor);
    }

    setShadowOpacity(currentOpacity);
  }, [enabled, currentBlur, currentColor, currentOpacity]);

  const createTextShadowValue = useCallback((blur: number, color: string, opacity: number) => {
    // Calculate offsets as 60% of blur value
    const offsetX = Math.round(blur * 0.6);
    const offsetY = Math.round(blur * 0.6);

    const colorWithOpacity = tinycolor(color)
      .setAlpha(opacity / 100)
      .toRgbString();

    return `${offsetX}px ${offsetY}px ${blur}px ${colorWithOpacity}`;
  }, []);

  const onToggleShadow = useCallback(() => {
    if (enabled) {
      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          textShadow: undefined
        }
      });
    } else {
      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          textShadow: createTextShadowValue(shadowBlur, shadowColor, shadowOpacity)
        }
      });
    }
  }, [
    enabled,
    clipId,
    clipData.asset_metadata.caption_styles?.pageStyle,
    shadowBlur,
    shadowColor,
    shadowOpacity,
    createTextShadowValue
  ]);

  const updateTextShadow = useCallback(
    (newValues: { blur?: number; color?: string; opacity?: number }) => {
      const updatedBlur = newValues.blur ?? shadowBlur;
      const updatedColor = newValues.color ?? shadowColor;
      const updatedOpacity = newValues.opacity ?? shadowOpacity;

      if (newValues.blur !== undefined) {
        setShadowBlur(newValues.blur);
      }
      if (newValues.color !== undefined) {
        setShadowColor(newValues.color);
      }
      if (newValues.opacity !== undefined) {
        setShadowOpacity(newValues.opacity);
      }

      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          textShadow: createTextShadowValue(updatedBlur, updatedColor, updatedOpacity)
        },
        wordStyle: {
          ...clipData.asset_metadata.caption_styles?.wordStyle,
          active: {
            ...clipData.asset_metadata.caption_styles?.wordStyle?.active,
            textShadow: undefined
          },
          inactive: {
            ...clipData.asset_metadata.caption_styles?.wordStyle?.inactive,
            textShadow: undefined
          }
        }
      });
    },
    [
      shadowBlur,
      shadowColor,
      shadowOpacity,
      clipId,
      clipData.asset_metadata.caption_styles?.pageStyle,
      clipData.asset_metadata.caption_styles?.wordStyle,
      createTextShadowValue
    ]
  );

  const onBlurChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateTextShadow({ blur: Number(e.target.value) });
    },
    [updateTextShadow]
  );

  const onShadowColorChange = useCallback(
    (value?: string) => {
      if (value) {
        updateTextShadow({ color: value });
      }
    },
    [updateTextShadow]
  );

  const onOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateTextShadow({ opacity: Number(e.target.value) });
    },
    [updateTextShadow]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="captions-menu-shadow-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconShadow size={16} />
              <PlayerMenuTooltip content="Shadow" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => setIsColorPickerOpen(false)}
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="rounded-lg border bg-white shadow-lg">
                {usePlatformBrandKit && isColorPickerOpen ? (
                  <ColorPicker
                    defaultColorsLabel="Shadow Color"
                    onChange={onShadowColorChange}
                    hex={shadowColor}
                    inline
                    onBack={() => setIsColorPickerOpen(false)}
                  />
                ) : (
                  <div className="space-y-4 p-4">
                    {/* Enable/Disable Shadow */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Shadow</span>
                      <Toggle on={enabled} onToggle={onToggleShadow} />
                    </div>

                    {enabled && (
                      <>
                        {/* Blur */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Blur</span>
                          <span className="col-span-8 flex items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={30}
                              value={shadowBlur}
                              step={1}
                              onChange={onBlurChange}
                              className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                            />
                            <span className="ml-1 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                              {shadowBlur}
                            </span>
                          </span>
                        </div>

                        {/* Shadow Color */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Color</span>
                          <ColorPicker
                            defaultColors={brandKitStore.colors}
                            defaultColorsLabel="Shadow Color"
                            onChange={onShadowColorChange}
                            hex={shadowColor}
                            onOpen={usePlatformBrandKit ? () => setIsColorPickerOpen(true) : undefined}
                            children={
                              <div className="flex size-full items-center justify-between bg-white p-2">
                                <div
                                  className="size-6 rounded border"
                                  style={{
                                    background: shadowColor
                                  }}
                                />
                                <span className="font-mono text-xs text-slate-400">{shadowColor}</span>
                              </div>
                            }
                            classNames="relative col-span-8 flex w-full items-center justify-between rounded-md border overflow-hidden"
                          />
                        </div>

                        {/* Opacity */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Opacity</span>
                          <span className="col-span-8 flex items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={shadowOpacity}
                              step={1}
                              onChange={onOpacityChange}
                              className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                            />
                            <span className="ml-1 w-6 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                              {shadowOpacity}%
                            </span>
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(CaptionShadowPopover);
